import React, { useEffect, useState, useRef, useCallback } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { auth, db } from "../lib/firebase";
import { useAuthState } from "react-firebase-hooks/auth";
import { doc, onSnapshot, getDoc } from "firebase/firestore";

import { useTranslation } from "react-i18next";
import navConfig, { actionIcons } from "../config/navbarConfig";
import * as notificationsService from "../services/notificationsService";
import * as chatService from "../services/chatService";
import { resolveUserDisplayName } from "../utils/userIdentity";
import { countUnreadChatsForUser } from "../utils/unread";
import { canUsePreferencesStorage } from "../utils/storageConsent";
import DesktopPrimaryNav from "./navbar/DesktopPrimaryNav";
import DesktopProfileMenu from "./navbar/DesktopProfileMenu";
import DesktopWaffleMenu from "./navbar/DesktopWaffleMenu";
import { errorDev } from '../utils/logger.js';

function Navbar() {
  const { t, i18n, ready: i18nReady } = useTranslation(['common']);
  const [user] = useAuthState(auth);
  const [profile, setProfile] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [waffleOpen, setWaffleOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [desktopSearchOpen, setDesktopSearchOpen] = useState(false);
  const [desktopSearchQuery, setDesktopSearchQuery] = useState("");
  const [desktopSearchCategory, setDesktopSearchCategory] = useState("all");
  const [cookieBannerOpen, setCookieBannerOpen] = useState(false);
  const [unreadCounts, setUnreadCounts] = useState({
    notifications: 0,
    messages: 0
  });

  const navigate = useNavigate();
  const location = useLocation();
  const profileRef = useRef();
  const langRef = useRef();
  const createRef = useRef();
  const desktopSearchInputRef = useRef();
  const profileMenuButtonRef = useRef();
  const profileMenuRef = useRef();
  const waffleRef = useRef();
  const waffleButtonRef = useRef();
  const waffleMenuRef = useRef();

  // Safe translation function with fallback and namespace support
  const safeT = (key, fallback = key, namespace = undefined) => {
    if (!i18nReady) return fallback;
    try {
      const result = namespace ? t(key, { ns: namespace }) : t(key);
      return result === key ? fallback : result;
    } catch (err) {
      errorDev("Operation failed:", err);
      return fallback;
    }
  };

  const {
    Settings,
    User,
    MessageSquare,
    Compass,
    Globe,
    Bell,
    PlusCircle,
    LogOut,
    Search,
    X,
    ShoppingBag,
    Home,
    ShoppingCart,
    Activity,
    Heart,
    Sprout,
    Utensils,
    Store,
    ChartNoAxesCombined,
    BadgeCheck,
    Shield
  } = actionIcons;

  const languages = navConfig?.languages ?? [];

  // 5-item mobile bottom navigation configuration
  const MOBILE_BOTTOM_NAV = [
    {
      id: "farm",
      label: "Farm",
      labelKey: "nav.farm",
      to: "/farm",
      Icon: Store,
      priority: 50,
    },
    {
      id: "farmpulse",
      label: "FarmPulse",
      labelKey: "nav.farmpulse",
      to: "/farmpulse",
      Icon: ChartNoAxesCombined,
      priority: 40,
    },
    {
      id: "post",
      label: "Create",
      labelKey: "nav.create",
      to: "/sellnow",
      Icon: PlusCircle,
      priority: 100,
      ariaLabel: "Sell product",
    },
    {
      id: "inbox",
      label: "Inbox",
      labelKey: "nav.inbox",
      to: "/inbox",
      Icon: MessageSquare,
      priority: 35,
    },
    {
      id: "me",
      label: "Me",
      labelKey: "nav.me",
      to: "/me",
      Icon: User,
      priority: 20,
    }
  ];

  // **FIX 1:** Remove /messages from hidden routes so bottom nav stays visible in chat views
  const HIDE_BOTTOM_NAV_PATTERNS = [
    /^\/sellnow$/,
    /^\/edit-product\/[^/]+$/,
  ];

  const hideMobileBottomNav = cookieBannerOpen || HIDE_BOTTOM_NAV_PATTERNS.some((pattern) =>
    pattern.test(location.pathname)
  );

  useEffect(() => {
    const handleCookieBannerToggle = (event) => {
      if (typeof event?.detail?.open === "boolean") {
        setCookieBannerOpen(event.detail.open);
      }
    };

    try {
      const current = document.body?.dataset?.cookieBannerOpen;
      setCookieBannerOpen(current === "true");
    } catch (err) {
      errorDev("Operation failed:", err);
      setCookieBannerOpen(false);
    }

    window.addEventListener("cookie-banner-toggle", handleCookieBannerToggle);
    return () => window.removeEventListener("cookie-banner-toggle", handleCookieBannerToggle);
  }, []);

  // Read cached profile quickly (for offline / slow)
  useEffect(() => {
    if (!user?.uid) return;
    try {
      if (!canUsePreferencesStorage()) return;
      const cached = localStorage.getItem(`me:profile:${user.uid}`);
      if (cached) setProfile(JSON.parse(cached));
    } catch (e) {
      // ignore
    }
  }, [user?.uid]);

  // Live snapshot + cache
  useEffect(() => {
    if (!user?.uid) {
      setProfile(null);
      return;
    }
    const ref = doc(db, "users", user.uid);

    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (snap.exists()) {
          const data = snap.data();
          setProfile(data);
          try {
            if (canUsePreferencesStorage()) {
              localStorage.setItem(`me:profile:${user.uid}`, JSON.stringify(data));
            }
          } catch (e) {}
        } else {
          setProfile(null);
        }
      },
      async () => {
        // On error, try direct fetch as fallback
        try {
          const s = await getDoc(ref);
          if (s.exists()) {
            setProfile(s.data());
            try {
              if (canUsePreferencesStorage()) {
                localStorage.setItem(`me:profile:${user.uid}`, JSON.stringify(s.data()));
              }
            } catch (err) { errorDev("Operation failed:", err);}
          }
        } catch (err) {
          // Silent fail - will retry on next mount
        errorDev("Operation failed:", err);
        }
      }
    );

    return () => {
      try {
        unsub();
      } catch (err) { errorDev("Operation failed:", err);}
    };
  }, [user?.uid]);

  // Set up real-time listeners for notifications and messages
  useEffect(() => {
    if (!user?.uid) {
      setUnreadCounts({ notifications: 0, messages: 0 });
      return;
    }

    const unsubscribers = [];

    // Real-time notifications listener
    const notifUnsubscribe = notificationsService.listenNotificationsForUser(
      user.uid,
      (notifs) => {
        const unreadNotifs = (notifs || []).filter((n) => notificationsService.isNotificationUnread(n)).length;
        setUnreadCounts(prev => ({ ...prev, notifications: unreadNotifs }));
      }
    );
    unsubscribers.push(notifUnsubscribe);

    // Real-time messages listener (chats collection)
    const messagesUnsubscribe = chatService.listenChatsForUser(user.uid, (chats) => {
        const unreadMessages = countUnreadChatsForUser(chats, user.uid);
        setUnreadCounts(prev => ({ ...prev, messages: unreadMessages }));
    });
    unsubscribers.push(messagesUnsubscribe);

    // Cleanup all listeners on unmount
    return () => {
      unsubscribers.forEach(unsub => {
        try {
          unsub();
        } catch (err) {
          // Silent cleanup error
        errorDev("Operation failed:", err);
        }
      });
    };
  }, [user?.uid]);

  // Close menus when clicking outside
  useEffect(() => {
    function handleClickOutside(e) {
      if (profileRef.current && !profileRef.current.contains(e.target)) setDropdownOpen(false);
      if (waffleRef.current && !waffleRef.current.contains(e.target)) setWaffleOpen(false);
      if (langRef.current && !langRef.current.contains(e.target)) setLangOpen(false);
      if (createRef.current && !createRef.current.contains(e.target)) {
        if (createOpen) setCreateOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [createOpen]);

  // Keyboard: Escape to close any open popover or sheet
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        setDropdownOpen(false);
        setWaffleOpen(false);
        setLangOpen(false);
        setCreateOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Prevent body scroll while create sheet is open
  useEffect(() => {
    if (createOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [createOpen]);

  const closeAll = useCallback(() => {
    setDropdownOpen(false);
    setWaffleOpen(false);
    setLangOpen(false);
    setCreateOpen(false);
  }, []);

  useEffect(() => {
    if (!dropdownOpen) return;

    const menu = profileMenuRef.current;
    if (!menu) return;

    const getFocusable = () => {
      return Array.from(
        menu.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])')
      ).filter((element) => !element.hasAttribute("disabled"));
    };

    const initialTarget = menu.querySelector('[data-menu-autofocus="true"]');
    if (initialTarget) {
      initialTarget.focus();
    }

    const handleMenuKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        setDropdownOpen(false);
        profileMenuButtonRef.current?.focus();
        return;
      }

      if (event.key !== "Tab") return;

      const focusable = getFocusable();
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    menu.addEventListener("keydown", handleMenuKeyDown);

    return () => {
      menu.removeEventListener("keydown", handleMenuKeyDown);
    };
  }, [dropdownOpen]);

  useEffect(() => {
    if (!waffleOpen) return;

    const menu = waffleMenuRef.current;
    if (!menu) return;

    const getFocusable = () => {
      return Array.from(
        menu.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])')
      ).filter((element) => !element.hasAttribute("disabled"));
    };

    const initialTarget = menu.querySelector('[data-menu-autofocus="true"]');
    if (initialTarget) {
      initialTarget.focus();
    }

    const handleMenuKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        setWaffleOpen(false);
        waffleButtonRef.current?.focus();
        return;
      }

      if (event.key !== "Tab") return;

      const focusable = getFocusable();
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    menu.addEventListener("keydown", handleMenuKeyDown);

    return () => {
      menu.removeEventListener("keydown", handleMenuKeyDown);
    };
  }, [waffleOpen]);

  const resolveRegionCode = () => {
    const profileCode =
      profile?.countryCode
      || profile?.location?.countryCode
      || profile?.location?.country
      || profile?.country;

    if (typeof profileCode === "string" && profileCode.trim().length >= 2) {
      return profileCode.trim().slice(0, 2).toUpperCase();
    }

    const locale = i18n.language || navigator.language || "en-US";
    const localeRegion = locale.includes("-") ? locale.split("-")[1] : "RW";
    return (localeRegion || "RW").slice(0, 2).toUpperCase();
  };

  const toFlagEmoji = (code) => {
    const cc = (code || "RW").toUpperCase();
    if (!/^[A-Z]{2}$/.test(cc)) return "🌍";
    const chars = [...cc].map((c) => 127397 + c.charCodeAt(0));
    return String.fromCodePoint(...chars);
  };

  const isDesktopTabActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const isDesktopActionActive = (path) => {
    if (!path) return false;
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const hideDesktopPrimaryNav = /^\/farm(?:\/|$)/.test(location.pathname);

  const handleLogout = async () => {
    try {
      await auth.signOut();
    } catch (err) {
      // Silent fail - will close and navigate anyway
    errorDev("Operation failed:", err);
    } finally {
      closeAll();
      navigate("/");
    }
  };

  const changeLanguage = (code) => {
    i18n.changeLanguage(code);
    try {
      localStorage.setItem("language", code);
    } catch (err) { errorDev("Operation failed:", err);}
    setLangOpen(false);
  };

  const openCreate = (type) => {
    setCreateOpen(false);
    if (type === "product") return navigate("/sellnow");
    navigate("/post");
  };

  const handleItemAction = (item) => {
    if (!item) return;
    if (typeof item.action === "function") return item.action();
    if (typeof item.action === "string") {
      switch (item.action) {
        case "create":
        case "toggleCreate":
          return setCreateOpen((s) => !s);
        case "openCreate":
          return setCreateOpen(true);
        case "openDropdown":
          return setDropdownOpen((s) => !s);
        default:
          if (item.action.startsWith && item.action.startsWith("/")) return navigate(item.action);
      }
    }
  };

  const handleDesktopSearchSubmit = (e) => {
    e.preventDefault();
    const q = desktopSearchQuery.trim();
    const params = new URLSearchParams();
    if (q) params.append("q", q);
    if (desktopSearchCategory && desktopSearchCategory !== "all") {
      params.append("cat", desktopSearchCategory);
    }
    navigate(`/search?${params.toString()}`);
  };

  const openDesktopSearch = () => {
    setDesktopSearchOpen(true);
    window.requestAnimationFrame(() => {
      desktopSearchInputRef.current?.focus();
    });
  };

  // Notification badge component
  const NotificationBadge = ({ count, className = "" }) => {
    if (!count || count <= 0) return null;

    return (
      <span className={`absolute -top-1 -right-1 inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-semibold leading-none text-white bg-red-600 rounded-full ${className}`}>
        {count > 9 ? '9+' : count}
      </span>
    );
  };

  const desktopTabs = [
    { id: "farm", label: "Farm", to: "/farm" },
    { id: "farmpulse", label: "FarmPulse", to: "/farmpulse" },
    { id: "farmmark", label: "FarmMark", to: "/farmmark" },
    { id: "northstar", label: "NorthStar", to: "/north-star" },
    { id: "about", label: "About", to: "/orrith-landing" },
    { id: "platform", label: "Our Platform", to: "/our-platform" },
    { id: "help-center", label: "Help Center", to: "/support" },
  ];

  const waffleItems = [
    {
      id: "farmmark",
      title: "FarmMark",
      subtitle: safeT(
        "navbar.waffle.farmmark",
        "Track your impact, climb leaderboards, and see your carbon savings."
      ),
      to: "/farmmark",
      Icon: BadgeCheck,
    },
    {
      id: "farmpulse",
      title: "FarmPulse",
      subtitle: safeT(
        "navbar.waffle.farmpulse",
        "Crop price trends, market forecasts, and planting insights."
      ),
       to: "/farmpulse",
      Icon: ChartNoAxesCombined,
    },
    {
      id: "northstar",
      title: "NorthStar",
      subtitle: safeT(
        "navbar.waffle.northstar",
        "Mission hub, impact feed, and story behind Orrith."
      ),
      to: "/north-star",
      Icon: Compass,
    },
    {
      id: "marketplace",
      title: "Marketplace",
      subtitle: safeT(
        "navbar.waffle.marketplace",
        "Offers, orders, and trusted trading."
      ),
      to: "/farm",
      Icon: Store,
    },
    {
      id: "orrith",
      title: "Orrith",
      subtitle: safeT(
        "navbar.waffle.orrith",
        "The center of certainty connecting people, produce, and impact with clarity."
      ),
      to: "/orrith-landing",
      Icon: Shield,
    },
  ];

  const dynamicLanguages = (() => {
    const configLanguages = (languages || []).filter((entry) => entry?.code);
    if (configLanguages.length > 0) {
      return configLanguages.map(({ code, label }) => ({
        code,
        label: label || code.toUpperCase(),
      }));
    }

    const supported = Array.isArray(i18n?.options?.supportedLngs)
      ? i18n.options.supportedLngs
      : [];

    return supported
      .filter((code) => code && code !== "cimode")
      .map((code) => ({ code, label: code.toUpperCase() }));
  })();

  const regionCode = resolveRegionCode();
  const regionFlag = toFlagEmoji(regionCode);
  const inboxUnreadCount = unreadCounts.notifications + unreadCounts.messages;

  return (
    <>
      <header className="hidden md:block sticky top-0 z-50 border-b border-green-800/70 bg-gradient-to-b from-green-900 via-green-900 to-green-950 text-white shadow-[0_10px_26px_rgba(0,0,0,0.35)]">
        <div className="max-w-screen-2xl mx-auto px-4 lg:px-6 pt-2 pb-1.5">
          <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3">
            <div className="justify-self-start flex items-center min-w-0">
              <div className="relative" ref={langRef}>
                <button
                  onClick={() => setLangOpen((s) => !s)}
                  className="h-10 w-10 rounded-full border border-emerald-300/70 bg-green-950/80 shadow-[0_0_0_2px_rgba(16,185,129,0.2)] flex items-center justify-center text-lg hover:border-emerald-200 transition"
                  aria-haspopup="true"
                  aria-expanded={langOpen}
                  aria-label="Change language and region"
                >
                  <span role="img" aria-label={regionCode}>{regionFlag}</span>
                </button>
                {langOpen && (
                  <div className="absolute left-0 mt-2 w-64 bg-white dark:bg-neutral-900 text-black dark:text-white rounded-xl shadow-xl z-50 border border-gray-200 dark:border-neutral-700 overflow-hidden">
                    <div className="px-4 py-3 border-b border-gray-200 dark:border-neutral-700">
                      <p className="text-sm font-semibold">Language & Region</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Region: {regionCode}</p>
                    </div>
                    <div className="py-1">
                      {dynamicLanguages.map(({ code, label }) => (
                        <button
                          key={code}
                          onClick={() => changeLanguage(code)}
                          className="w-full px-4 py-2.5 text-left hover:bg-gray-100 dark:hover:bg-neutral-800 flex items-center justify-between"
                        >
                          <span className="text-sm">{label}</span>
                          {i18n.language && i18n.language.startsWith(code) && (
                            <span className="text-green-600 text-xs font-semibold">Active</span>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

            </div>

            <button
              onClick={() => {
                navigate("/");
                closeAll();
              }}
              className="justify-self-center px-2 py-1.5 hover:opacity-90 transition"
              aria-label="Go to Orrith home"
            >
              <div className="text-center leading-none">
                <div className="text-[25px] font-extrabold tracking-[0.06em] text-emerald-100">Orrith</div>
                <div className="mt-0.5 text-[10px] uppercase tracking-[0.2em] text-emerald-200/90">Cycle of Certainty</div>
              </div>
            </button>

            <div className="justify-self-end flex items-center gap-2">
              {user ? (
                  <>
                    <div className="h-10 rounded-full border border-green-500 bg-green-600 text-white inline-flex items-stretch overflow-hidden">
                      <Link
                        to="/sellnow"
                        onClick={closeAll}
                        className={`px-4 text-sm font-semibold inline-flex items-center justify-center gap-1.5 transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300 focus-visible:ring-inset ${
                          isDesktopActionActive("/sellnow")
                            ? "bg-green-200 text-green-950"
                            : "text-white hover:bg-green-700"
                        }`}
                        aria-label={safeT("navbar.sellNow", "Sell now")}
                      >
                        <PlusCircle size={14} aria-hidden="true" />
                        {safeT("navbar.sellNow", "Sell now")}
                      </Link>
                    </div>

                  <Link
                    to="/inbox"
                    onClick={closeAll}
                    className={`relative h-10 w-10 rounded-full border transition inline-flex items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300 focus-visible:ring-offset-2 focus-visible:ring-offset-green-900 ${
                      isDesktopActionActive("/inbox")
                        ? "bg-emerald-300 text-green-950 border-emerald-200"
                        : "bg-green-950/70 text-emerald-50 border-emerald-300/55 hover:border-emerald-200 hover:bg-green-900/75"
                    }`}
                    aria-label={safeT("navbar.openInbox", "Open inbox")}
                    title={safeT("navbar.inbox", "Inbox")}
                  >
                    <MessageSquare size={18} aria-hidden="true" />
                    <NotificationBadge count={inboxUnreadCount} />
                  </Link>

                  <DesktopProfileMenu
                    user={user}
                    profile={profile}
                    profileRef={profileRef}
                    menuRef={profileMenuRef}
                    buttonRef={profileMenuButtonRef}
                    dropdownOpen={dropdownOpen}
                    setDropdownOpen={setDropdownOpen}
                    closeAll={closeAll}
                    handleLogout={handleLogout}
                    safeT={safeT}
                    resolveUserDisplayName={resolveUserDisplayName}
                    t={t}
                  />

                  <DesktopWaffleMenu
                    waffleItems={waffleItems}
                    waffleOpen={waffleOpen}
                    setWaffleOpen={setWaffleOpen}
                    waffleRef={waffleRef}
                    menuRef={waffleMenuRef}
                    buttonRef={waffleButtonRef}
                    closeAll={closeAll}
                    safeT={safeT}
                  />
                </>
              ) : (
                <>
                  <Link
                    to="/signup"
                    className="h-9 px-4 inline-flex items-center rounded-full bg-emerald-300 text-green-950 hover:bg-emerald-200 font-semibold text-sm"
                  >
                    Join
                  </Link>
                  <Link
                    to="/login"
                    className="h-9 px-4 inline-flex items-center rounded-full border border-emerald-200/70 text-emerald-50 hover:border-emerald-100 text-sm"
                  >
                    Sign in
                  </Link>
                </>
              )}
            </div>
          </div>

          {!hideDesktopPrimaryNav && (
            <DesktopPrimaryNav
              desktopTabs={desktopTabs}
              isDesktopTabActive={isDesktopTabActive}
              desktopSearchOpen={desktopSearchOpen}
              openDesktopSearch={openDesktopSearch}
              desktopSearchInputRef={desktopSearchInputRef}
              handleDesktopSearchSubmit={handleDesktopSearchSubmit}
              desktopSearchCategory={desktopSearchCategory}
              setDesktopSearchCategory={setDesktopSearchCategory}
              desktopSearchQuery={desktopSearchQuery}
              setDesktopSearchQuery={setDesktopSearchQuery}
              setDesktopSearchOpen={setDesktopSearchOpen}
              SearchIcon={Search}
              CloseIcon={X}
            />
          )}
        </div>
      </header>

      {/* 5-ITEM MOBILE BOTTOM NAV */}
      {!hideMobileBottomNav && (
      <nav className="sm:hidden fixed bottom-0 inset-x-0 bg-white dark:bg-neutral-900 border-t border-gray-200 dark:border-neutral-700 z-50">
        <div className="max-w-screen-xl mx-auto px-2">
          <div className="relative">
            <div className="grid grid-cols-5 gap-0 items-center py-1">
              {MOBILE_BOTTOM_NAV.map((item, index) => {
                if (item.id === "post")
                  return (
                    <div key={item.id} className="col-span-1 flex justify-center relative">
                      <Link
                        to={item.to}
                        onClick={() => setCreateOpen(false)}
                        className="-mt-4 bg-green-700 text-white w-12 h-12 rounded-full shadow-xl flex items-center justify-center ring-4 ring-white dark:ring-neutral-900 transition-transform hover:scale-105 active:scale-95"
                        aria-label={safeT("navbar.create", "Create")}
                      >
                        <PlusCircle size={22} />
                      </Link>
                    </div>
                  );

                return (
                  <Link key={item.id} to={item.to} onClick={() => handleItemAction(item)}
                    className="flex flex-col items-center justify-center text-xs text-gray-700 dark:text-gray-200 py-1 px-1 relative min-h-[56px] transition-colors hover:bg-gray-50 dark:hover:bg-neutral-800 rounded-lg"
                    aria-label={safeT(item.labelKey, item.label)}
                  >
                    <div className="relative">
                      {item.id === "me" && user && (profile?.photoURL || profile?.avatar) ? (
                        <img
                          src={profile.photoURL || profile.avatar}
                          alt=""
                          className="w-6 h-6 rounded-full object-cover ring-1 ring-gray-200 dark:ring-neutral-600"
                          onError={(e) => { e.currentTarget.src = "/default-avatar.png"; }}
                        />
                      ) : item.Icon ? (
                        <item.Icon size={20} />
                      ) : (
                        <div className="w-5 h-5" />
                      )}

                      {item.id === "inbox" && (
                        <NotificationBadge count={unreadCounts.notifications + unreadCounts.messages} />
                      )}

                    </div>
                    <span className="text-[10px] mt-0.5 text-center leading-tight font-medium">
                      {safeT(item.labelKey, item.label)}
                    </span>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>

        {/* Create sheet overlay */}
        {createOpen && (
          <div className="fixed inset-0 z-50 flex items-end justify-center">
            <div
              className="absolute inset-0 bg-black/40 transition-opacity duration-300"
              onClick={() => setCreateOpen(false)}
              aria-hidden
            />
            <div 
              ref={createRef} 
              role="dialog" 
              aria-modal="true" 
              className="relative w-full max-w-lg transform transition-transform duration-300 ease-out"
              style={{ transform: createOpen ? 'translateY(0)' : 'translateY(100%)' }}
            >
              <div className="bg-white dark:bg-neutral-800 rounded-t-xl p-4 shadow-lg">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">{safeT("navbar.createNew", "Create New")}</h3>
                  <button 
                    onClick={() => setCreateOpen(false)} 
                    className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-neutral-700"
                    aria-label={safeT("common:close", "Close")}
                  >
                    <X size={20} />
                  </button>
                </div>

                <div className="grid gap-3">
                   <button
                     onClick={() => openCreate("product")}
                     className="bg-green-700 hover:bg-green-800 text-white font-semibold py-4 rounded-xl transition flex items-center justify-center gap-2"
                     aria-label={safeT("navbar.sellNow", "Sell now")}
                   >
                     <ShoppingBag size={20} />
                     {safeT("navbar.sellNow", "Sell now")}
                   </button>
                 </div>
              </div>
            </div>
          </div>
        )}
      </nav>
      )}

      {/* Add padding to main content to account for bottom nav */}
      <style jsx="true">{`
        .mobile-bottom-nav-padding {
          padding-bottom: calc(var(--mobile-bottom-nav-height) + env(safe-area-inset-bottom));
        }
      `}</style>
    </>
  );
}

export default Navbar;